import { DateTime } from "luxon";
import { TagType } from "src/constants";
import { QueryParams } from "./utilities";

const queryKeys = {
  analytics: {
    getDailyPageViewSummary: (params?: { startDateUtc?: string; endDateUtc?: string }) =>
      [{ key: "dailyPageViewSummary", ...params }] as const,
    getPageMetrics: (params?: { startDateUtc?: string; endDateUtc?: string; page?: number; pageSize?: number }) =>
      [{ key: "pageMetrics", ...params }] as const,
  },
  actionItems: {
    all: [{ scope: "actionItems" }] as const,
    lists: () => [{ ...queryKeys.actionItems.all[0], format: "list" }] as const,
    userOwnershipLists: () => [{ ...queryKeys.actionItems.lists()[0], entity: "user" }] as const,
    userOwnershipList: (userId: string | undefined) =>
      [{ ...queryKeys.actionItems.userOwnershipLists()[0], userId }] as const,
    userOwnershipListWithOptions: (userId: string | undefined, queryParams?: QueryParams) =>
      [{ ...queryKeys.actionItems.userOwnershipList(userId)[0], queryParams }] as const,
    item: (guid: string | undefined) => [{ ...queryKeys.actionItems.all[0], guid }] as const,
    detail: (guid: string | undefined) => [{ ...queryKeys.actionItems.item(guid)[0], format: "detail" }] as const,
    summary: (guid: string | undefined) => [{ ...queryKeys.actionItems.item(guid)[0], format: "summary" }] as const,
    feed: (guid: string | undefined) => [{ ...queryKeys.actionItems.item(guid)[0], format: "feed" }] as const,
  },
  azureDevOpsWorkItems: {
    getAzureDevOpsWorkItemCollections: () => ["azureDevOpsWorkItemCollections"] as const,
    getAzureDevOpsWorkItemCollection: (azureDevOpsWorkItemCollectionGuid: string | null) =>
      [
        ...queryKeys.azureDevOpsWorkItems.getAzureDevOpsWorkItemCollections(),
        azureDevOpsWorkItemCollectionGuid,
      ] as const,
  },
  comments: {
    getCommentsFeed: (threadGuid: string | undefined) => [{ key: "commentsFeed", threadGuid }] as const,
  },
  forms: {
    getForm: (guid: string | undefined) => [{ key: "form", guid }] as const,
    getForms: () => [{ key: "forms" }] as const,
  },
  formSubmissions: {
    getFormSubmissions: () => [{ scope: "formSubmissions" }] as const,
    getFormSubmissionsWithQueryParams: (queryParams?: QueryParams) =>
      [{ ...queryKeys.formSubmissions.getFormSubmissions()[0], queryParams }] as const,
    getFormSubmission: (formSubmissionGuid: string | null) =>
      [{ scope: "formSubmission", formSubmissionGuid }] as const,
    getFormSubmissionsReport: () => [{ scope: "formSubmissionsReport" }] as const,
    getFormSubmissionsReportWithQueryParams: (queryParams?: QueryParams) =>
      [{ ...queryKeys.formSubmissions.getFormSubmissionsReport()[0], queryParams }] as const,
  },
  issues: {
    getIssueDetail: (guid: string) => [{ key: "issueDetail", guid }] as const,
    getIssueFeed: (guid: string | undefined) => [{ key: "issueFeed", guid }] as const,
    getIssues: (teamSlug: string | undefined, userId: string | undefined) =>
      [{ key: "issues", teamSlug, userId }] as const,
    getIssuesWithOptions: (teamSlug: string | undefined, userId: string | undefined, queryParams?: QueryParams) =>
      [
        {
          ...queryKeys.issues.getIssues(teamSlug, userId)[0],
          queryParams,
        },
      ] as const,
  },
  meetings: {
    getActiveMeetingsForUser: () => [{ key: "activeMeetingsForUser" }] as const,
    getOneOnOneMeetingsForAllUsers: () => [{ key: "oneOnOneMeetingsForUser" }] as const,
    getOneOnOneMeetingsForUser: (userId?: string) =>
      [{ ...queryKeys.meetings.getOneOnOneMeetingsForAllUsers()[0], userId }] as const,
    getMeeting: (meetingGuid: string) => [{ key: "meeting", meetingGuid }] as const,
    getMeetingParticipants: (meetingGuid: string) => [{ key: "meetingParticipants", meetingGuid }] as const,
    getMeetingsForAllTeams: () => [{ key: "teamMeetings" }] as const,
    getMeetingsForTeam: (teamGuid: string) =>
      [{ ...queryKeys.meetings.getMeetingsForAllTeams()[0], teamGuid }] as const,
    getMeetingNotes: (meetingGuid: string) => [{ key: "meetingNotes", meetingGuid }] as const,
    getSharedMeetingNotes: (meetingGuid: string) =>
      [{ ...queryKeys.meetings.getMeetingNotes(meetingGuid)[0], visibility: "shared" }] as const,
    getPrivateMeetingNotes: (meetingGuid: string) =>
      [{ ...queryKeys.meetings.getMeetingNotes(meetingGuid)[0], visibility: "private" }] as const,
    getAgendaItemTextContent: (agendaItemGuid: string) => [{ key: "agendaStepTextContent", agendaItemGuid }] as const,
    getAgendaItemResources: (agendaItemGuid: string) => [{ key: "agendaStepResources", agendaItemGuid }] as const,
  },
  metrics: {
    getMetricCollections: () => ["metricCollections"] as const,
    getMetricCollection: (metricCollectionGuid: string | null) =>
      [...queryKeys.metrics.getMetricCollections(), metricCollectionGuid] as const,
    getMetric: (metricGuid: string) => [{ key: "metric", metricGuid }] as const,
    getMetricFeed: (metricGuid: string) => [{ key: "metricFeed", metricGuid }] as const,
  },
  organization: {
    getOrganizationNetworkGraph: () => [{ key: "organizationNetworkGraph" }] as const,
  },
  plans: {
    getPlans: () => [{ key: "plans" }] as const,
    getPlanVersions: (slug: string) => [{ key: "planVersions", slug }] as const,
    getPlanVersion: (slug: string | undefined, planVersionId: string | undefined, showChanges: boolean) =>
      [{ key: "planVersion", slug, planVersionId, showChanges }] as const,
    allPlans: [{ key: "plan" }] as const,
    getPlan: (planGuid: string | undefined, date?: string | null, offsetInMinutes?: number | null) =>
      [{ key: "plan", planGuid, date, offsetInMinutes }] as const,
    getPlanWithOptions: (
      planGuid: string | undefined,
      date?: string | null,
      offsetInMinutes?: number | null,
      queryParams?: QueryParams,
    ) =>
      [
        {
          ...queryKeys.plans.getPlan(planGuid, date, offsetInMinutes)[0],
          queryParams,
        },
      ] as const,
    getPlanSubitems: (planGuid: string | undefined) => [{ key: "planSubitems", planGuid }] as const,
    getPlanSubitemsWithOptions: (planGuid: string | undefined, queryParams?: QueryParams) =>
      [{ ...queryKeys.plans.getPlanSubitems(planGuid)[0], queryParams }] as const,
    getPlanHistory: (guid: string, offsetInMinutes: number) => [{ key: "planHistory", guid, offsetInMinutes }] as const,
    getPlanActionItems: (slug: string | undefined) => [{ key: "planActionItems", slug }] as const,
    getStructure: (guid: string | undefined) => [{ key: "planStructure", guid }] as const,
  },
  reports: {
    getOrganizationReport: (
      includeRoles?: boolean,
      includeRoleTags?: boolean,
      includeTeams?: boolean,
      includeParentTeams?: boolean,
    ) => [{ key: "organizationReport", includeRoles, includeRoleTags, includeTeams, includeParentTeams }] as const,
  },
  resources: {
    getResourcesForTeam: (slug: string) => [{ key: "teamResources", slug }] as const,
  },
  roles: {
    getRole: (roleGuid: string) => [{ key: "role", roleGuid }] as const,
    allUserRoles: () => [{ key: "userRoles" }] as const,
    getRolesForUser: (userId?: string) => [{ ...queryKeys.roles.allUserRoles()[0], userId }] as const,
    getOpenRoles: () => [{ key: "openRoles" }] as const,
    getRoleFeed: (roleGuid: string) => [{ key: "roleFeed", roleGuid }] as const,
  },
  search: {
    getSearchResults: (query: string | null, filter: string | null) => [{ key: "search", query, filter }] as const,
  },
  surveys: {
    getSurveys: (userId?: string) => [{ key: "surveys", userId }] as const,
    getSurvey: (guid: string | undefined) => [{ key: "survey", guid }] as const,
    getSurveyInstances: (surveyGuid: string) => [{ key: "surveyInstances", surveyGuid }] as const,
    getSurveyInstance: (guid: string | undefined | null) => [{ key: "surveyInstance", guid }] as const,
    getSurveyQuestions: (guid: string) => [{ key: "surveyQuestions", guid }] as const,
    getSurveyQuestion: (guid: string | undefined) => [{ key: "surveyQuestion", guid }] as const,
    getSurveyAnswersReport: (surveyGuid: string, surveyInstanceGuid?: string) =>
      [{ key: "surveyResponses", surveyGuid, surveyInstanceGuid }] as const,
    getSurveyCoherenceReportAll: () => [{ key: "surveyCoherenceReport" }] as const,
    getSurveyCoherenceReport: (
      selectedSurveyInstanceGuid: string | undefined,
      comparisonSurveyInstanceGuid: string | undefined,
      selectedTeamGuids: string[] | undefined,
      comparisonTeamGuids: string[] | undefined,
    ) =>
      [
        {
          key: "surveyCoherenceReport",
          selectedSurveyInstanceGuid,
          comparisonSurveyInstanceGuid,
          selectedTeamGuids,
          comparisonTeamGuids,
        },
      ] as const,
    getCoherenceScoreOverTime: (
      selectedTeamGuids: string[] | undefined,
      comparisonTeamGuids: string[] | undefined,
      numberOfMonths: number,
      includeResponseRate: boolean,
    ) =>
      [
        {
          key: "coherenceScoreOverTime",
          selectedTeamGuids,
          comparisonTeamGuids,
          numberOfMonths,
          includeResponseRate,
        },
      ] as const,
    getItemsCreatedFromResponseAnswer: (responseAnswerGuid: string) =>
      [{ key: "itemsCreatedFromResponseAnswer", responseAnswerGuid }] as const,
    getSurveyInstanceResponse: (instanceGuid: string | undefined) =>
      [{ key: "surveyInstanceResponse", instanceGuid }] as const,
  },
  tags: {
    getTags: (type: TagType) => [{ key: "tags", type }] as const,
  },
  taskItems: {
    getAllTaskItems: (taskItemGuid: string | undefined) => [{ key: "taskItems", taskItemGuid }] as const,
    getTaskItemDetail: (taskItemGuid: string | undefined) => [{ key: "taskItemDetail", taskItemGuid }] as const,
    getTaskItemSubtasks: (taskItemGuid: string | undefined) => [{ key: "taskItemSubtasks", taskItemGuid }] as const,
    getTaskItemFeed: (taskItemGuid: string | undefined) => [{ key: "taskItemFeed", taskItemGuid }] as const,
    allTeamTaskItems: ["teamTaskItems"] as const,
    allTeamTaskItemSubtasks: ["teamTaskItemSubtasks"] as const,
    getTeamTaskItems: (teamSlug: string | undefined, queryParams?: QueryParams) =>
      [...queryKeys.taskItems.allTeamTaskItems, teamSlug, queryParams] as const,
    getTeamTaskItemSubtasks: (teamSlug: string | undefined, queryParams?: QueryParams) =>
      [...queryKeys.taskItems.allTeamTaskItemSubtasks, teamSlug, queryParams] as const,
    allUserTaskItems: ["userTaskItems"] as const,
    getUserTaskItems: (userGuid: string | undefined, queryParams?: QueryParams) =>
      [...queryKeys.taskItems.allUserTaskItems, userGuid, queryParams] as const,
    getIssueTaskItems: (issueGuid: string | undefined) => [{ key: "issueTaskItems", issueGuid }] as const,
  },
  teams: {
    getTeams: (userId?: string) => [{ key: "teams", userId }] as const,
    getTeamsTree: (startDateTimeUtc?: DateTime, endDateTimeUtc?: DateTime) =>
      [{ key: "teamsTree", startDateTimeUtc, endDateTimeUtc }] as const,
    getTeam: (slug?: string, userId?: string) => {
      const queryKey: Record<string, string> = {
        key: "team",
      };

      if (slug !== undefined) {
        queryKey.slug = slug;
      }

      if (userId !== undefined) {
        queryKey.userId = userId;
      }

      return [queryKey] as const;
    },
    getConnectedTeams: (slug: string) => [{ key: "connectedTeams", slug }] as const,
    getTeamStatistics: (slug: string | undefined) => [{ key: "teamStatistics", slug }] as const,
    getTeamNetworkGraph: (slug: string | undefined) => [{ key: "teamNetworkGraph", slug }] as const,
    getTeamNetworkList: (slug: string | undefined) => [{ key: "teamNetworkList", slug }] as const,
    getTeamFeed: (teamGuid: string | undefined) => [{ key: "teamFeed", teamGuid }] as const,
  },
  users: {
    getUser: (userId?: string) => [{ key: "users", userId }] as const,
    getUsers: () => [{ key: "users" }] as const,
    getUserStatistics: (userId?: string) => [{ key: "userStatistics", userId }] as const,
    getNotificationPreferences: (userId?: string) => [{ key: "notificationPreferences", userId }] as const,
    getNotifications: (userId?: string) => [{ key: "notifications", userId }] as const,
    getNotificationCount: (userId?: string) => [{ key: "notificationCount", userId }] as const,
    getSession: (azureAdUserId: string | undefined) => [{ key: "session", azureAdUserId }] as const,
  },
  wiki: {
    getWikiPage: (slug: string) => [{ key: "wikiPage", slug }] as const,
  },
};

export { queryKeys };
